import { IS_WEB } from 'constants/platform'
import React, { useMemo } from 'react'

import { ActionIcon } from 'partials/PostCard/subcomponents'
import { MediaPreview } from 'partials/WorkoutEditorForm/subcomponents/MultipleMediaSelector/widgets/MediaPreview'
import { AddStepButton } from 'partials/WorkoutEditorForm/subcomponents/WorkoutStepsForm/AddStepButton'

import { Box } from 'widgets/Box'
import { Pressable } from 'widgets/Pressable'
import { Text } from 'widgets/Text'

import { getFileTypeFromMimeType } from 'utils/media'
import commonStyles from 'utils/styles/commonStyles'

type StepRowProps = {
  title: string
  description?: string
  file?: any
  isMuted: boolean
  onDescriptionEdit: () => void
  onMediaEdit: () => void
  onDelete: () => void
  onInsert: () => void
  onMuteToggle: () => void
}

export const WorkoutStepRow = ({
  title,
  description,
  file,
  isMuted,
  onDescriptionEdit,
  onMediaEdit,
  onDelete,
  onInsert,
  onMuteToggle,
}: StepRowProps) => {
  const calculateFileUrlForWeb = () => {
    if (!file) {
      return undefined
    }
    return 'url' in file ? file.url : URL.createObjectURL(file)
  }

  const calculateFileUrlForMobile = () => {
    if (!file) {
      return undefined
    }
    if ('url' in file) {
      return file.url
    }
    let { uri } = file
    if (uri && !uri.includes('file://')) {
      uri = `file://${file.uri}`
    }
    return uri
  }

  const calculateFileTypeForWeb = () => {
    if (!file) return undefined
    return 'mimeType' in file
      ? getFileTypeFromMimeType(file.mimeType)
      : getFileTypeFromMimeType(file.type)
  }

  const calculateFileTypeForMobile = () => {
    if (!file) return undefined
    return getFileTypeFromMimeType(file.mimeType)
  }

  const fileUrl = useMemo(() => {
    return IS_WEB ? calculateFileUrlForWeb() : calculateFileUrlForMobile()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file])

  const fileType = useMemo(() => {
    return IS_WEB ? calculateFileTypeForWeb() : calculateFileTypeForMobile()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file])

  return (
    <Box padding={{ horizontal: 'md' }} height={164}>
      <Box height={130} flexDirection='row'>
        <Box flex={1}>
          <Box
            flex={1}
            align='center'
            justifyContent='center'
            backgroundColor={file ? 'greyDarkest' : 'grey'}
            borderRadius='sm'
            style={commonStyles.overflowHidden}
          >
            {file && (
              <MediaPreview
                isMuted={isMuted}
                hidePlayPauseButton
                fileUrl={fileUrl}
                type={fileType}
              />
            )}
            <Box flex={1} align='center' justifyContent='center' position='absolute'>
              <ActionIcon icon='camera' color='white' action={onMediaEdit} />
            </Box>
          </Box>
        </Box>

        <Box flex={3} margin={{ left: 'smd' }}>
          <Box flexDirection='row' align='center' justifyContent='space-between' height={30}>
            <Box margin={{ top: 'sm' }}>
              <Text bold color='black'>
                {title}
              </Text>
            </Box>
            <ActionIcon icon='x' iconSize={24} action={onDelete} color='greyDark' />
          </Box>
          <Box flex={1} flexDirection='row'>
            <Box flex={1} flexDirection='row' margin={{ top: 'sm' }}>
              <Pressable onPress={onDescriptionEdit} style={commonStyles.fullSize}>
                <Text
                  color={description?.length ? 'greyDarkest' : 'greyDark'}
                  fontSize='sm'
                  numberOfLines={4}
                >
                  {description?.length ? description : 'Add description...'}
                </Text>
              </Pressable>
            </Box>

            <Box width={24} align='flex-end' justifyContent='flex-end'>
              {fileType === 'video' && (
                <ActionIcon
                  icon={!isMuted ? 'volume-2' : 'volume-x'}
                  iconSize={24}
                  action={onMuteToggle}
                  color='greyDark'
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <AddStepButton onPress={onInsert} />
    </Box>
  )
}
